import { useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Search, Plus, Eye, Brain, Code, Image, Cpu, MoreHorizontal, Pencil, Trash2, RefreshCw, Loader2, Sparkles, Lock, Globe, AudioLines,
} from 'lucide-react';
import { ModelAccessManager } from './ModelAccessManager';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAdminModels, useCreateModel, useUpdateModel, useDeleteModel, useSyncModels } from '@/hooks/useAdminModels';
import type { LlmModel } from '@/hooks/useModels';
import type { Json } from '@/integrations/supabase/types';

// ---------- helpers ----------

function getCaps(cap: Json): Record<string, boolean> {
  if (typeof cap === 'object' && cap !== null && !Array.isArray(cap)) return cap as Record<string, boolean>;
  return {};
}

function formatContextWindow(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return String(n);
}

const PROVIDER_COLORS: Record<string, string> = {
  openai: 'bg-emerald-600/15 text-emerald-600 border-emerald-600/30',
  anthropic: 'bg-purple-600/15 text-purple-600 border-purple-600/30',
  google: 'bg-blue-600/15 text-blue-600 border-blue-600/30',
  deepseek: 'bg-zinc-600/15 text-zinc-400 border-zinc-600/30',
};

// ---------- form schema ----------

const modelSchema = z.object({
  id: z.string().min(1, 'ID é obrigatório'),
  provider: z.string().min(1, 'Provider é obrigatório'),
  display_name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  context_window: z.coerce.number().int().positive().optional().or(z.literal('')),
  price_tier: z.string().optional(),
  sort_order: z.coerce.number().int().default(0),
  is_active: z.boolean().default(false),
  cap_vision: z.boolean().default(false),
  cap_audio: z.boolean().default(false),
  cap_thinking: z.boolean().default(false),
  cap_code: z.boolean().default(false),
  cap_image_gen: z.boolean().default(false),
  is_recommended: z.boolean().default(false),
  recommendation_note: z.string().optional(),
  access_level: z.enum(['public', 'restricted']).default('public'),
});

type ModelFormValues = z.infer<typeof modelSchema>;

// ---------- component ----------

export function ModelsManagementPanel() {
  const { data: models = [], isLoading } = useAdminModels();
  const updateModel = useUpdateModel();
  const deleteModel = useDeleteModel();
  const createModel = useCreateModel();

  const syncModels = useSyncModels();

  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<LlmModel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LlmModel | null>(null);

  const handleSync = async () => {
    try {
      const data = await syncModels.mutateAsync();
      toast.success(`Sincronização concluída: ${data.inserted} novos modelos, ${data.updated} atualizados (${data.total} total)`);
    } catch (e: any) {
      toast.error(e.message || 'Erro ao sincronizar');
    }
  };


  const providers = useMemo(() => [...new Set((models as LlmModel[]).map((m) => m.provider))], [models]);

  const filtered = useMemo(() => {
    let list = models as LlmModel[];
    if (providerFilter !== 'all') list = list.filter((m) => m.provider === providerFilter);
    if (statusFilter === 'active') list = list.filter((m) => m.is_active);
    if (statusFilter === 'inactive') list = list.filter((m) => !m.is_active);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((m) => m.display_name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q));
    }
    return list;
  }, [models, providerFilter, statusFilter, search]);

  const activeCount = (models as LlmModel[]).filter((m) => m.is_active).length;

  // --- inline sort_order edit ---
  const handleSortOrderChange = (id: string, value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    updateModel.mutate({ id, sort_order: num }, {
      onSuccess: () => toast.success('Ordem atualizada'),
      onError: (e: any) => toast.error(e.message),
    });
  };

  // --- toggle active ---
  const handleToggleActive = (model: LlmModel) => {
    updateModel.mutate({ id: model.id, is_active: !model.is_active }, {
      onSuccess: () => toast.success(model.is_active ? 'Modelo desativado' : 'Modelo ativado'),
      onError: (e: any) => toast.error(e.message),
    });
  };

  // --- inline price tier ---
  const handlePriceTierChange = (id: string, value: string) => {
    updateModel.mutate({ id, price_tier: value || null }, {
      onError: (e: any) => toast.error(e.message),
    });
  };

  // --- toggle recommended ---
  const handleToggleRecommended = (model: LlmModel) => {
    const isRec = (model as any).is_recommended;
    updateModel.mutate(
      { id: model.id, is_recommended: !isRec, ...(!isRec ? {} : { recommendation_note: null }) } as any,
      {
        onSuccess: () => toast.success(isRec ? 'Recomendação removida' : 'Modelo recomendado'),
        onError: (e: any) => toast.error(e.message),
      }
    );
  };

  // --- delete ---
  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteModel.mutate(deleteTarget.id, {
      onSuccess: () => { toast.success('Modelo excluído'); setDeleteTarget(null); },
      onError: (e: any) => toast.error(e.message),
    });
  };

  // --- open edit ---
  const openEdit = (model: LlmModel) => {
    setEditingModel(model);
    setFormOpen(true);
  };

  const openCreate = () => {
    setEditingModel(null);
    setFormOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats + add button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-secondary">
          <span>{activeCount} modelos ativos</span>
          <span>•</span>
          <span>{(models as LlmModel[]).length} modelos total</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleSync} disabled={syncModels.isPending} className="gap-1.5">
            {syncModels.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {syncModels.isPending ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
          <Button size="sm" onClick={openCreate} className="gap-1.5">
            <Plus size={14} /> Adicionar Modelo
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
          <input
            type="text"
            placeholder="Buscar por nome ou ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-36 h-9">
            <SelectValue placeholder="Provedor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {providers.map((p) => (
              <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center space-y-3">
          <Cpu className="h-10 w-10 text-tertiary" />
          <p className="text-secondary text-sm font-medium">Nenhum modelo encontrado</p>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSync} disabled={syncModels.isPending}>
            {syncModels.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {syncModels.isPending ? 'Sincronizando...' : 'Sincronizar com OpenRouter'}
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-color overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead className="w-16">Ordem</TableHead>
                <TableHead className="w-16">Ativo</TableHead>
                <TableHead className="w-16">⭐</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="w-28">Provedor</TableHead>
                <TableHead className="w-24">Acesso</TableHead>
                <TableHead className="w-24">Preço</TableHead>
                <TableHead className="w-24">Contexto</TableHead>
                <TableHead className="w-28">Capabilities</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((model) => {
                const caps = getCaps(model.capabilities);
                return (
                  <TableRow key={model.id} className={!model.is_active ? 'opacity-50' : ''}>
                    <TableCell>
                      <SortOrderInput defaultValue={model.sort_order} onCommit={(v) => handleSortOrderChange(model.id, v)} />
                    </TableCell>
                    <TableCell>
                      <Switch checked={model.is_active} onCheckedChange={() => handleToggleActive(model)} />
                    </TableCell>
                    <TableCell>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleToggleRecommended(model)}
                              className={`p-1 rounded transition-colors ${(model as any).is_recommended ? 'text-amber-500' : 'text-tertiary hover:text-secondary'}`}
                            >
                              <Sparkles size={16} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="text-xs">
                            {(model as any).is_recommended ? 'Remover recomendação' : 'Marcar como recomendado'}
                            {(model as any).recommendation_note && <><br />{(model as any).recommendation_note}</>}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <div>
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-semibold text-primary text-sm cursor-default">{model.display_name}</span>
                            </TooltipTrigger>
                            {model.description && (
                              <TooltipContent side="top" className="max-w-xs text-xs">{model.description}</TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <p className="text-xs text-tertiary mt-0.5 font-mono">{model.id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${PROVIDER_COLORS[model.provider] ?? ''}`}>
                        {model.provider}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {(model as any).access_level === 'restricted' ? (
                        <Badge variant="outline" className="text-xs gap-1 bg-amber-500/10 text-amber-600 border-amber-500/30">
                          <Lock size={11} /> Restrito
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs gap-1 text-tertiary">
                          <Globe size={11} /> Público
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select value={model.price_tier ?? ''} onValueChange={(v) => handlePriceTierChange(model.id, v)}>
                        <SelectTrigger className="h-7 w-20 text-xs">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="$">$</SelectItem>
                          <SelectItem value="$$">$$</SelectItem>
                          <SelectItem value="$$$">$$$</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-secondary">{formatContextWindow(model.context_window)}</TableCell>
                    <TableCell>
                      <TooltipProvider delayDuration={200}>
                        <div className="flex items-center gap-1.5">
                          {caps.vision && <Tooltip><TooltipTrigger asChild><span><Eye size={14} className="text-tertiary" /></span></TooltipTrigger><TooltipContent className="text-xs">Visão</TooltipContent></Tooltip>}
                          {caps.audio && <Tooltip><TooltipTrigger asChild><span><AudioLines size={14} className="text-tertiary" /></span></TooltipTrigger><TooltipContent className="text-xs">Áudio</TooltipContent></Tooltip>}
                          {caps.thinking && <Tooltip><TooltipTrigger asChild><span><Brain size={14} className="text-tertiary" /></span></TooltipTrigger><TooltipContent className="text-xs">Raciocínio</TooltipContent></Tooltip>}
                          {caps.code && <Tooltip><TooltipTrigger asChild><span><Code size={14} className="text-tertiary" /></span></TooltipTrigger><TooltipContent className="text-xs">Código</TooltipContent></Tooltip>}
                          {caps.image_gen && <Tooltip><TooltipTrigger asChild><span><Image size={14} className="text-tertiary" /></span></TooltipTrigger><TooltipContent className="text-xs">Geração de imagem</TooltipContent></Tooltip>}
                        </div>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 text-secondary hover:text-primary transition-colors">
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(model)} className="gap-2">
                            <Pencil size={14} /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteTarget(model)} className="gap-2 text-red-500 focus:text-red-500">
                            <Trash2 size={14} /> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Form dialog */}
      <ModelFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        model={editingModel}
        onCreate={createModel}
        onUpdate={updateModel}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir modelo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{deleteTarget?.display_name}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Sort order inline input ----------

function SortOrderInput({ defaultValue, onCommit }: { defaultValue: number; onCommit: (v: string) => void }) {
  const [value, setValue] = useState(String(defaultValue));
  const ref = useRef<HTMLInputElement>(null);

  const commit = () => {
    if (value !== String(defaultValue)) onCommit(value);
  };

  return (
    <input
      ref={ref}
      type="number"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); ref.current?.blur(); } }}
      className="w-14 h-7 px-1.5 text-center text-sm rounded border border-color bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
    />
  );
}

// ---------- Model form dialog ----------

function ModelFormDialog({
  open, onOpenChange, model, onCreate, onUpdate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  model: LlmModel | null;
  onCreate: ReturnType<typeof useCreateModel>;
  onUpdate: ReturnType<typeof useUpdateModel>;
}) {
  const isEdit = !!model;
  const caps = model ? getCaps(model.capabilities) : {};

  const form = useForm<ModelFormValues>({
    resolver: zodResolver(modelSchema),
    defaultValues: model
      ? {
          id: model.id,
          provider: model.provider,
          display_name: model.display_name,
          description: model.description ?? '',
          context_window: model.context_window ?? ('' as any),
          price_tier: model.price_tier ?? '',
          sort_order: model.sort_order,
          is_active: model.is_active,
          cap_vision: !!caps.vision,
          cap_audio: !!caps.audio,
          cap_thinking: !!caps.thinking,
          cap_code: !!caps.code,
          cap_image_gen: !!caps.image_gen,
          is_recommended: !!(model as any).is_recommended,
          recommendation_note: (model as any).recommendation_note ?? '',
          access_level: ((model as any).access_level ?? 'public') as 'public' | 'restricted',
        }
      : {
          id: '',
          provider: '',
          display_name: '',
          description: '',
          context_window: '' as any,
          price_tier: '',
          sort_order: 0,
          is_active: false,
          cap_vision: false,
          cap_audio: false,
          cap_thinking: false,
          cap_code: false,
          cap_image_gen: false,
          is_recommended: false,
          recommendation_note: '',
          access_level: 'public',
        },
  });

  // Reset form when model changes
  const prevModelId = useRef(model?.id);
  if (prevModelId.current !== model?.id) {
    prevModelId.current = model?.id;
    if (model) {
      const c = getCaps(model.capabilities);
      form.reset({
        id: model.id,
        provider: model.provider,
        display_name: model.display_name,
        description: model.description ?? '',
        context_window: model.context_window ?? ('' as any),
        price_tier: model.price_tier ?? '',
        sort_order: model.sort_order,
        is_active: model.is_active,
        cap_vision: !!c.vision,
        cap_audio: !!c.audio,
        cap_thinking: !!c.thinking,
        cap_code: !!c.code,
        cap_image_gen: !!c.image_gen,
        is_recommended: !!(model as any).is_recommended,
        recommendation_note: (model as any).recommendation_note ?? '',
        access_level: ((model as any).access_level ?? 'public') as 'public' | 'restricted',
      });
    } else {
      form.reset({
        id: '', provider: '', display_name: '', description: '',
        context_window: '' as any, price_tier: '', sort_order: 0,
        is_active: false, cap_vision: false, cap_audio: false, cap_thinking: false,
        cap_code: false, cap_image_gen: false,
        is_recommended: false, recommendation_note: '',
        access_level: 'public',
      });
    }
  }

  const onSubmit = (values: ModelFormValues) => {
    const capabilities: Record<string, boolean> = {};
    if (values.cap_vision) capabilities.vision = true;
    if (values.cap_audio) capabilities.audio = true;
    if (values.cap_thinking) capabilities.thinking = true;
    if (values.cap_code) capabilities.code = true;
    if (values.cap_image_gen) capabilities.image_gen = true;

    const payload: any = {
      id: values.id,
      provider: values.provider,
      display_name: values.display_name,
      description: values.description || null,
      context_window: values.context_window ? Number(values.context_window) : null,
      price_tier: values.price_tier || null,
      sort_order: values.sort_order,
      is_active: values.is_active,
      is_recommended: values.is_recommended,
      recommendation_note: values.recommendation_note || null,
      access_level: values.access_level,
      capabilities,
    };

    if (isEdit) {
      const { id, ...updates } = payload;
      onUpdate.mutate({ id: model!.id, ...updates }, {
        onSuccess: () => { toast.success('Modelo atualizado'); onOpenChange(false); },
        onError: (e: any) => toast.error(e.message),
      });
    } else {
      onCreate.mutate(payload, {
        onSuccess: () => { toast.success('Modelo criado'); onOpenChange(false); form.reset(); },
        onError: (e: any) => toast.error(e.message),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-color shrink-0">
          <DialogTitle>{isEdit ? 'Editar modelo' : 'Adicionar modelo'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="model-id" className="text-xs">ID do modelo</Label>
              <Input id="model-id" placeholder="openai/gpt-4o" {...form.register('id')} disabled={isEdit} className="h-9" />
              {form.formState.errors.id && <p className="text-xs text-red-500">{form.formState.errors.id.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="model-provider" className="text-xs">Provider</Label>
              <Input id="model-provider" placeholder="openai" {...form.register('provider')} className="h-9" />
              {form.formState.errors.provider && <p className="text-xs text-red-500">{form.formState.errors.provider.message}</p>}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="model-name" className="text-xs">Display Name</Label>
            <Input id="model-name" placeholder="GPT-4o" {...form.register('display_name')} className="h-9" />
            {form.formState.errors.display_name && <p className="text-xs text-red-500">{form.formState.errors.display_name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="model-desc" className="text-xs">Descrição (opcional)</Label>
            <Textarea id="model-desc" placeholder="Modelo multimodal da OpenAI..." {...form.register('description')} rows={2} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="model-ctx" className="text-xs">Context Window</Label>
              <Input id="model-ctx" type="number" placeholder="128000" {...form.register('context_window')} className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Price Tier</Label>
              <Select value={form.watch('price_tier') ?? ''} onValueChange={(v) => form.setValue('price_tier', v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="$">$</SelectItem>
                  <SelectItem value="$$">$$</SelectItem>
                  <SelectItem value="$$$">$$$</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="model-sort" className="text-xs">Sort Order</Label>
              <Input id="model-sort" type="number" {...form.register('sort_order')} className="h-9" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Capabilities</Label>
            <div className="flex flex-wrap gap-4">
              {([
                ['cap_vision', 'Visão'],
                ['cap_audio', 'Áudio'],
                ['cap_thinking', 'Raciocínio'],
                ['cap_code', 'Código'],
                ['cap_image_gen', 'Geração de imagem'],
              ] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
                  <Checkbox
                    checked={form.watch(key)}
                    onCheckedChange={(v) => form.setValue(key, !!v)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2 rounded-md border border-color p-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.watch('is_recommended')}
                onCheckedChange={(v) => form.setValue('is_recommended', v)}
              />
              <Label className="text-sm flex items-center gap-1.5">
                <Sparkles size={14} className="text-amber-500" />
                Recomendado
              </Label>
            </div>
            {form.watch('is_recommended') && (
              <div className="space-y-1.5 pl-1">
                <Label htmlFor="rec-note" className="text-xs">Motivo da recomendação</Label>
                <Input
                  id="rec-note"
                  placeholder="Ex: Ideal para geração de imagens"
                  {...form.register('recommendation_note')}
                  className="h-8 text-sm"
                />
              </div>
            )}
          </div>

          {isEdit && model ? (
            <ModelAccessManager
              modelId={model.id}
              accessLevel={form.watch('access_level')}
              onAccessLevelChange={(lvl) => {
                form.setValue('access_level', lvl);
                // Persist immediately so grants reflect the new state without requiring Save
                onUpdate.mutate({ id: model.id, access_level: lvl } as any, {
                  onError: (e: any) => toast.error(e.message),
                });
              }}
            />
          ) : (
            <p className="text-xs text-tertiary">
              O controle de acesso (público/restrito) ficará disponível após criar o modelo.
            </p>
          )}

          <div className="flex items-center gap-2">
            <Switch
              checked={form.watch('is_active')}
              onCheckedChange={(v) => form.setValue('is_active', v)}
            />
            <Label className="text-sm">Ativo</Label>
          </div>

          </div>

          <div className="shrink-0 border-t border-color px-6 py-3 flex justify-end gap-2 bg-background shadow-[0_-8px_12px_-8px_hsl(0_0%_0%/0.15)]">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={onCreate.isPending || onUpdate.isPending}>
              {isEdit ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
